import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@heroui/react';
import { RadioButtonGroup } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import { PAYMENT_METHODS, type PaymentMethod } from '../paymentMeta';
import { useFocusMethodGroup } from './useFocusMethodGroup';

type PaymentMethodSelectProps = {
    method: PaymentMethod;
    onSelect: (method: PaymentMethod) => void;
    /** Whether the payment dialog is open — focuses the selected method once, on the transition into open (bos-seller#58: the method group has initial focus). */
    isOpen: boolean;
    /** Reads the group's keys on the capture phase, ahead of its own arrow handling (`useCombinedLegKeys`: `↑`/`↓` enter the split legs). */
    onKeyDownCapture: (event: React.KeyboardEvent) => void;
};

/**
 * The three-up payment-method picker (render-only): compact icon + label cards in
 * a single-select group, the chosen one carrying the accent ring. `secondary`
 * variant so the cards read correctly against the modal surface. `←`/`→`
 * switch the selection natively — the items are real grouped radio inputs.
 * Initial focus (`useFocusMethodGroup`) is the one piece of orchestration this
 * View needs; it lives in its own hook so this component stays testable by
 * props alone.
 */
export const PaymentMethodSelect: React.FC<PaymentMethodSelectProps> = ({
    method,
    onSelect,
    isOpen,
    onKeyDownCapture,
}) => {
    const { t } = useTranslation('pages');
    const groupRef = useFocusMethodGroup(isOpen);

    return (
        // A plain wrapper for `onKeyDownCapture`: `RadioButtonGroup` takes no
        // keyboard handlers of its own.
        <div onKeyDownCapture={onKeyDownCapture}>
            <RadioButtonGroup
                aria-label={t('seller.payment.method')}
                // `px-0.5` keeps the selected-item ring off the scroll body's
                // clip edge — without it the leftmost/rightmost tiles' rings
                // get cut.
                className="grid-cols-3 px-0.5"
                layout="grid"
                onChange={(value) => onSelect(value as PaymentMethod)}
                ref={groupRef}
                value={method}
                variant="secondary"
            >
                {PAYMENT_METHODS.map((item) => (
                    <RadioButtonGroup.Item
                        key={item.key}
                        {...getNodeTestIdProp(`seller-pay-method-${item.key}`)}
                        value={item.key}
                    >
                        <RadioButtonGroup.ItemContent className="items-center gap-2 text-center">
                            <Icon
                                className="size-6 text-muted"
                                icon={item.icon}
                            />
                            <Label className="text-xs font-semibold">
                                {t(item.labelKey)}
                            </Label>
                        </RadioButtonGroup.ItemContent>
                    </RadioButtonGroup.Item>
                ))}
            </RadioButtonGroup>
        </div>
    );
};
